// Modeled on frontend/src/api/client.ts (ApiError shape, global 401 handler,
// same-origin cookies). The device-heap workarounds (GET coalescing,
// concurrency cap) are deliberately dropped — the cloud API has no such
// constraints.
const BASE = ""; // same origin: vite proxy in dev, served by the cloud backend in prod

export interface ApiError {
  status: number;
  message: string;
  body?: unknown;
  /** Machine-readable reason from a structured detail ({"code": "..."}). */
  code?: string;
  /** Set when the backend rejected with code=upgrade_required. */
  requiredPlan?: string;
  feature?: string;
  /** Seconds to wait, from a 429's Retry-After header. */
  retryAfter?: number;
}

export type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(h: UnauthorizedHandler | null) {
  onUnauthorized = h;
}

export function isUpgradeRequired(err: unknown): err is ApiError {
  return !!err && typeof err === "object" && "requiredPlan" in err &&
    (err as ApiError).requiredPlan !== undefined;
}

/** Narrow an unknown/Error-typed rejection (e.g. from react-query) to ApiError. */
export function asApiError(err: unknown): ApiError {
  if (err && typeof err === "object" && "status" in err && "message" in err) {
    return err as ApiError;
  }
  return { status: 0, message: err instanceof Error ? err.message : String(err) };
}

// FastAPI/pydantic puts where the bad value came from first ("body",
// "query", "path"); the field path after it is what a person recognizes.
const LOC_SOURCES = new Set(["body", "query", "path", "header", "cookie"]);

function formatValidationItem(item: unknown): string | null {
  if (!item || typeof item !== "object") return typeof item === "string" ? item : null;
  const { loc, msg } = item as { loc?: unknown; msg?: unknown };
  if (typeof msg !== "string") return null;
  const text = msg.replace(/^Value error,\s*/i, "");
  const path = Array.isArray(loc)
    ? loc.filter((p, i) => !(i === 0 && typeof p === "string" && LOC_SOURCES.has(p))).join(".")
    : "";
  return path ? `${path}: ${text}` : text;
}

/** A readable message from a FastAPI error `detail`, whatever its shape:
 *  a plain string, a structured `{detail, code, ...}` object, or a 422's
 *  array of pydantic validation errors (`[{loc, msg, type}]`). Never returns
 *  raw JSON. */
export function formatErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map(formatValidationItem).filter((p): p is string => !!p);
    return parts.length ? parts.join("; ") : fallback;
  }
  if (detail && typeof detail === "object") {
    const inner = (detail as { detail?: unknown; message?: unknown }).detail
      ?? (detail as { message?: unknown }).message;
    if (inner !== undefined && inner !== detail) return formatErrorDetail(inner, fallback);
  }
  return fallback;
}

/** Build the ApiError for a non-2xx response body (pure; exported for tests). */
export function buildApiError(status: number, statusText: string, raw: string): ApiError {
  let body: unknown = raw;
  try {
    body = raw ? JSON.parse(raw) : raw;
  } catch {
    /* not JSON */
  }
  const isObj = typeof body === "object" && body !== null;
  const detail = isObj && "detail" in (body as object)
    ? (body as { detail: unknown }).detail
    : undefined;
  // A non-JSON body (proxy error page) is not something to show verbatim.
  const fallback = statusText || `HTTP ${status}`;
  const message = detail !== undefined
    ? formatErrorDetail(detail, fallback)
    : typeof body === "string" && body && body.length <= 200 && !/^\s*</.test(body)
      ? body
      : fallback;
  const err: ApiError = { status, message, body };
  const structured = typeof detail === "object" && detail !== null && !Array.isArray(detail)
    ? (detail as { code?: unknown; required_plan?: unknown; feature?: unknown })
    : null;
  const code = structured?.code ?? (isObj ? (body as { code?: unknown }).code : undefined);
  if (typeof code === "string") err.code = code;
  // Structured 403: {"detail": {"code": "upgrade_required", "required_plan": "..."}}
  if (structured && structured.code === "upgrade_required") {
    err.requiredPlan = typeof structured.required_plan === "string" ? structured.required_plan : "";
    err.feature = typeof structured.feature === "string" ? structured.feature : undefined;
  }
  return err;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
      ...init,
    });
  } catch (e) {
    const err: ApiError = {
      status: 0,
      message: "Server unreachable. Please try again.",
      body: (e as Error)?.message,
    };
    throw err;
  }
  if (res.status === 401) {
    if (onUnauthorized) onUnauthorized();
  }
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    const err = buildApiError(res.status, res.statusText, raw);
    const retry = Number(res.headers.get("Retry-After"));
    if (res.status === 429 && Number.isFinite(retry) && retry > 0) err.retryAfter = retry;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined });
}

export function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}
